import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { Tournament, Player } from '@swiss-magic/shared-types';
import type { TournamentLifecycle } from '../useTournamentLifecycle';
import { updateSpotConfirmationMode } from '@/lib/api';
import { ApiError } from '@/lib/api/core';
import { showErrorToast } from '@/lib/toast';

// Story 082/083 — registration summary. Counts come from the players
// list already fetched by the hub page; the CTAs advance the
// registration phase so the whole lifecycle is drivable from the hub.

interface Props {
  tournament: Tournament;
  players: Player[] | null;
  lifecycle: TournamentLifecycle;
  /** Story 093 — registration lifecycle actions are owner-only; player
   *  management stays available to co-directors. */
  isOwner: boolean;
  /** Story 100 — refetch the tournament after the mode changes. */
  onTournamentChanged: () => void;
}

function countByStatus(players: Player[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const p of players) {
    out[p.status] = (out[p.status] ?? 0) + 1;
  }
  return out;
}

export function RegistrationPanel({
  tournament,
  players,
  lifecycle,
  isOwner,
  onTournamentChanged,
}: Props) {
  const { t } = useTranslation('director');
  const navigate = useNavigate();
  const [savingMode, setSavingMode] = useState(false);

  // Story 100 — the mode is a registration setting flippable on a LIVE
  // tournament (the edit form is draft-only by design).
  async function handleModeChange(mode: 'manual' | 'auto') {
    setSavingMode(true);
    try {
      await updateSpotConfirmationMode(tournament.id, mode);
      onTournamentChanged();
    } catch (err) {
      showErrorToast(
        err instanceof ApiError ? err.userMessage : 'Failed to update',
      );
    } finally {
      setSavingMode(false);
    }
  }
  const modeEditable =
    isOwner &&
    ['draft', 'published', 'registration_open', 'registration_closed'].includes(
      tournament.status,
    );
  const counts = players ? countByStatus(players) : null;
  const status = tournament.status;
  const isEmpty = !players || players.length === 0;

  const managePlayers = (
    <Button
      size="sm"
      variant="outline"
      data-testid="tournament-hub-manage-players-button"
      onClick={() => navigate(`/tournaments/${tournament.id}/players`)}
    >
      {t('tournament_hub.panels.registration.manage_players')}
    </Button>
  );

  return (
    <Card data-testid="tournament-hub-panel-registration">
      <CardHeader>
        <CardTitle className="text-base">
          {t('tournament_hub.panels.registration.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Counts grid — always renders the four buckets, zero when missing */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {(['accepted', 'pending', 'withdrawn', 'rejected'] as const).map(
            (bucket) => {
              const value =
                bucket === 'pending'
                  ? counts?.['none'] ?? 0
                  : counts?.[bucket] ?? 0;
              return (
                <div
                  key={bucket}
                  className="rounded border bg-background p-2 text-center"
                >
                  <div
                    data-testid={`tournament-hub-registration-count-${bucket}`}
                    className="text-xl font-semibold"
                  >
                    {value}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t(`tournament_hub.panels.registration.bucket.${bucket}`)}
                  </div>
                </div>
              );
            },
          )}
        </div>

        {/* Story 100 — spot-confirmation mode (owner, pre-start). */}
        {modeEditable && (
          <div className="flex flex-wrap items-center gap-2">
            <label
              htmlFor="hubSpotConfirmationMode"
              className="text-sm text-muted-foreground"
            >
              {t('tournament_hub.panels.registration.spot_confirmation_label')}
            </label>
            <select
              id="hubSpotConfirmationMode"
              data-testid="hub-spot-confirmation-mode-select"
              value={tournament.spotConfirmationMode}
              disabled={savingMode}
              onChange={(e) =>
                handleModeChange(e.target.value as 'manual' | 'auto')
              }
              className="h-8 rounded-md border border-input bg-transparent px-2 text-sm dark:bg-input/30"
            >
              <option value="manual">
                {t('tournament_form.spot_confirmation_manual')}
              </option>
              <option value="auto">
                {t('tournament_form.spot_confirmation_auto')}
              </option>
            </select>
          </div>
        )}

        {/* Phase-aware actions. Lifecycle (open/close/reopen/publish/
            unpublish) is owner-only; co-directors keep player management. */}
        {isOwner && (status === 'draft' || status === 'published') && (
          <div className="flex flex-col items-start gap-2">
            <p className="text-sm text-muted-foreground">
              {status === 'draft'
                ? t('tournament_hub.panels.registration.empty_draft')
                : t('tournament_hub.panels.registration.empty_published')}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                data-testid="tournament-hub-open-registration-cta"
                onClick={lifecycle.handleOpenRegistration}
              >
                {t('tournament_hub.panels.registration.open_registration')}
              </Button>
              {status === 'draft' ? (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="tournament-hub-publish-only-button"
                  onClick={() => lifecycle.setPublishOpen(true)}
                >
                  {t('tournament_hub.panels.registration.publish_only')}
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="tournament-hub-unpublish-button"
                  onClick={lifecycle.handleUnpublish}
                >
                  {t('tournament_hub.panels.registration.unpublish')}
                </Button>
              )}
            </div>
          </div>
        )}

        {status === 'registration_open' && (
          <div className="flex flex-wrap gap-2">
            {isOwner && (
              <Button
                size="sm"
                data-testid="tournament-hub-close-registration-button"
                onClick={lifecycle.handleCloseRegistration}
              >
                {t('tournament_hub.panels.registration.close_registration')}
              </Button>
            )}
            {managePlayers}
            {isOwner && (
              <Button
                size="sm"
                variant="outline"
                data-testid="tournament-hub-unpublish-button"
                onClick={lifecycle.handleUnpublish}
              >
                {t('tournament_hub.panels.registration.unpublish')}
              </Button>
            )}
          </div>
        )}

        {status === 'registration_closed' && (
          <div className="flex flex-wrap gap-2">
            {isOwner && (
              <Button
                size="sm"
                data-testid="tournament-hub-reopen-registration-button"
                onClick={lifecycle.handleReopenRegistration}
              >
                {t('tournament_hub.panels.registration.reopen_registration')}
              </Button>
            )}
            {managePlayers}
          </div>
        )}

        {(status === 'in_progress' || status === 'completed') && !isEmpty && (
          <Button
            size="sm"
            variant="outline"
            data-testid="tournament-hub-view-players-button"
            onClick={() => navigate(`/tournaments/${tournament.id}/players`)}
          >
            {t('tournament_hub.panels.registration.view_players')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
